import i18n from './i18n'
import { registerCityNames } from './i18n/cityName'

const BASE = (import.meta.env.VITE_API_URL ?? '') + '/api/v1'

// The app's language as the API's locale codes (en, fr, pt-br, zh-hans...).
const apiLocale = () => String(i18n.language || 'en').toLowerCase()

// ── Deep link resolution ───────────────────────────────────────────────────────

export async function fetchVenue(venueId) {
  const res = await fetch(`${BASE}/venues/${encodeURIComponent(venueId)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch venue')
  const data = await res.json()
  return data.venue
}

export async function fetchCityVenues(slug) {
  const res = await fetch(`${BASE}/cities/${encodeURIComponent(slug)}/venues`)
  if (res.status === 404) return []
  if (!res.ok) throw new Error('Failed to fetch venues')
  const data = await res.json()
  return data.venues ?? []
}

export async function fetchCityBySlug(slug) {
  const res = await fetch(`${BASE}/cities/by-slug/${encodeURIComponent(slug)}`)
  if (res.status === 404) {
    // A city merged into another: the one it lives in now.
    const moved = await res.json().catch(() => null)
    return moved?.code === 'city_merged' && moved.slug && moved.slug !== slug ? fetchCityBySlug(moved.slug) : null
  }
  if (!res.ok) throw new Error('Failed to resolve city slug')
  return res.json() // { channelId, city, country, timezone, slug }
}

export async function fetchEventById(eventId) {
  const res = await fetch(`${BASE}/events/${encodeURIComponent(eventId)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch event')
  return res.json() // { event, cityName, country, timezone }
}

export async function createGuestSession(nickname) {
  const res = await fetch(`${BASE}/guest/session`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ nickname }),
  })
  if (!res.ok) throw new Error('Failed to create session')
  return res.json()
}

// Optional `country` (ISO-2) lets the backend constrain nearest-city to the
// same country, preventing cross-border snaps. The caller is expected to
// resolve it via Nominatim (see reverseGeocodeCountry in App.jsx). When
// omitted/null, backend falls back to global nearest.
//
// propose: an account's city confirmed by GPS or chosen by hand never moves:
// the answer's switchFrom is that city when GPS found another (the app asks
// "Switch?"). No city within 50 km → an error with code 'no_city' (the picker).
export async function resolveLocation(lat, lng, country) {
  const body = { lat, lng, propose: true, locale: apiLocale(), ...(country ? { country } : {}) }
  const res = await fetch(`${BASE}/location/resolve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw await cityError(res, 'Failed to resolve location')
  return registerCityNames(await res.json())
}

/** An Error carrying the API's code (no_city, city_change_limit...). */
async function cityError(res, message) {
  const data = await res.json().catch(() => null)
  return Object.assign(new Error(data?.error || message), { code: data?.code ?? null, status: res.status })
}

// ── World cities (every GeoNames city, created at the first visit) ─────────────

/** Cities whose name starts with q, any language or alias: [{geonameId, name, country, population, playsIn, channelId}]. */
export async function searchCities(q, { signal } = {}) {
  const res = await fetch(`${BASE}/cities/search?q=${encodeURIComponent(q)}&locale=${encodeURIComponent(apiLocale())}`, { signal })
  if (!res.ok) throw new Error('Failed to search cities')
  const data = await res.json()
  return data.cities ?? []
}

/** The Hilads city a searched city plays in (Brooklyn → New York), created when needed. */
export async function pickCity(geonameId) {
  const res = await fetch(`${BASE}/cities/pick`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ geonameId, locale: apiLocale() }),
  })
  if (!res.ok) throw await cityError(res, 'Failed to pick city')
  return registerCityNames(await res.json())
}

/** Set where an account is ({channelId} or {geonameId}): returns the city; code city_change_limit after 10 a day. */
export async function setMyCity(pick) {
  const res = await fetch(`${BASE}/me/city`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ...pick, locale: apiLocale() }),
  })
  if (!res.ok) throw await cityError(res, 'Failed to set city')
  return registerCityNames(await res.json())
}

/** One city by its channel id (a merged city answers with its target, mergedFrom), or null. */
export async function fetchCityById(id) {
  const res = await fetch(`${BASE}/cities/by-id/${encodeURIComponent(id)}?locale=${encodeURIComponent(apiLocale())}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch city')
  return registerCityNames(await res.json())
}

// Lightweight country-only reverse-geocode via Nominatim (zoom=3 = country
// level - fast, no city lookup). Returns ISO-2 uppercase or null on any
// failure. Caller passes the result straight into resolveLocation; failure
// is non-fatal - backend uses global nearest when country is absent.
export async function reverseGeocodeCountry(lat, lng) {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&zoom=3`,
      { headers: { 'Accept-Language': 'en' } },
    )
    if (!res.ok) return null
    const data = await res.json()
    const cc = data?.address?.country_code
    return cc ? cc.toUpperCase() : null
  } catch {
    return null
  }
}

export async function fetchMessages(channelId, { beforeId, limit } = {}) {
  const params = new URLSearchParams()
  if (limit)    params.set('limit', limit)
  if (beforeId) params.set('before_id', beforeId)
  const qs  = params.size ? '?' + params : ''
  const res = await fetch(`${BASE}/channels/${channelId}/messages${qs}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch messages')
  return res.json() // { messages, hasMore, onlineUsers, onlineCount }
}

// Lean messages fetch - skips presence + badge enrichment on the server.
// Used as one half of the parallel join+messages critical path.
// Badges are enriched deferred via fetchMessageBadges after first render.
export async function fetchLeanMessages(channelId, { beforeId, limit = 50 } = {}) {
  const params = new URLSearchParams({ lean: '1', limit: String(limit) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/channels/${channelId}/messages?${params}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch messages')
  return res.json() // { messages, hasMore }
}

// ── World channel (global companion channel) ────────────────────────────────
export async function fetchWorldMessages({ beforeId, limit = 50 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/world/messages?${params}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch world messages')
  return res.json() // { messages, hasMore }
}

export async function sendWorldMessage(guestId, nickname, content, mentions = null, replyToMessageId = null) {
  const body = { guestId, nickname, content }
  if (mentions && mentions.length) body.mentions = mentions
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  const res = await fetch(`${BASE}/world/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to send world message')
  return res.json()
}

// Toggle an emoji reaction on a World message (own endpoint; same shape as city).
export async function toggleWorldReaction(messageId, emoji, guestId) {
  const res = await fetch(`${BASE}/world/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji, guestId }),
  })
  if (!res.ok) throw new Error('Failed to toggle world reaction')
  const data = await res.json()
  return data.reactions ?? []
}

// Recent arrivals across all cities → { arrivals: [{ nickname, guestId, userId, city, country, createdAt }] }
export async function fetchWorldArrivals() {
  try {
    const res = await fetch(`${BASE}/world/arrivals`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json()
    return data.arrivals || []
  } catch { return [] }
}

// Cached server-side (~45s). { online, cities, crossCity: { count, cities[] } }
export async function fetchWorldActivity() {
  const res = await fetch(`${BASE}/world/activity`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch world activity')
  return res.json()
}

// Recent international (cross-city) challenges for the World hero carousel.
export async function fetchWorldChallenges() {
  try {
    const res = await fetch(`${BASE}/world/challenges`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json()
    return data.challenges ?? []
  } catch {
    return []
  }
}

// ALL international challenges worldwide (full card DTOs) for the "See all" view.
export async function fetchWorldChallengesAll(limit = 60) {
  try {
    const res = await fetch(`${BASE}/world/challenges/all?limit=${limit}`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json()
    return data.challenges ?? []
  } catch {
    return []
  }
}

// Mark a channel (city integer id or the string 'world') read up to now.
export async function markChannelRead(channelId, guestId) {
  try {
    await fetch(`${BASE}/read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ channelId, guestId }),
    })
  } catch { /* non-fatal: unread is a convenience, never blocks the UI */ }
}

// Quiet-city fallback context for a city. → { cityQuiet, worldActive }
export async function fetchQuietContext(cityChannelId) {
  try {
    const res = await fetch(`${BASE}/world/quiet-context?city=${encodeURIComponent(cityChannelId)}`, { credentials: 'include' })
    if (!res.ok) return { cityQuiet: false, worldActive: false }
    return res.json()
  } catch { return { cityQuiet: false, worldActive: false } }
}

// Batch unread counts. channelIds = [cityChannelId, 'world', ...]. → { [id]: count }
export async function fetchUnread(channelIds, guestId) {
  const params = new URLSearchParams()
  for (const c of channelIds) params.append('channels[]', String(c))
  if (guestId) params.set('guestId', guestId)
  const res = await fetch(`${BASE}/unread?${params}`, { credentials: 'include' })
  if (!res.ok) return {}
  const data = await res.json()
  return data.unread || {}
}

// POST /me/city - commit a manual city switch as users.current_city_id.
// Backend bypasses the two-signal rule and sets the city immediately. Errors
// are swallowed: the local UI switch is the source of truth for this frame;
// the next /location/resolve will reconcile if the backend write failed.
export async function setCurrentCity(channelId) {
  try {
    const res = await fetch(`${BASE}/me/city`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ channelId }),
    })
    if (!res.ok) console.warn('[me/city] non-ok', res.status)
  } catch (err) {
    console.warn('[me/city] failed', err)
  }
}

export async function joinChannel(channelId, sessionId, guestId, nickname, previousChannelId = null) {
  const body = { sessionId, guestId, nickname }
  if (previousChannelId) body.previousChannelId = previousChannelId
  const res = await fetch(`${BASE}/channels/${channelId}/join`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Platform': 'web' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to join channel')
  return res.json()
}

export async function bootstrapChannel(channelId, sessionId, guestId, nickname, previousChannelId = null) {
  const body = { sessionId, guestId, nickname }
  if (previousChannelId) body.previousChannelId = previousChannelId
  // lean=1: skip badge enrichment + auth queries on the critical path.
  // Web fetches badges via fetchMessageBadges after first render.
  const res = await fetch(`${BASE}/channels/${channelId}/bootstrap?lean=1`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Platform': 'web' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to bootstrap channel')
  const data = await res.json()
  return {
    joinMessage:         data.joinMessage         ?? null,
    messages:            data.messages            ?? [],
    hasMore:             data.hasMore             ?? false,
    onlineUsers:         data.onlineUsers         ?? [],
    onlineCount:         data.onlineCount         ?? 0,
    hasUnreadDMs:        data.hasUnreadDMs        ?? null,
    unreadNotifications: data.unreadNotifications ?? null,
  }
}

// Fetches badge data for registered message authors - called after first render
// to enrich the chat feed without blocking the initial bootstrap.
export async function fetchMessageBadges(channelId, userIds) {
  if (!userIds || userIds.length === 0) return {}
  const qs = userIds.map(id => `ids[]=${encodeURIComponent(id)}`).join('&')
  try {
    const res = await fetch(`${BASE}/channels/${channelId}/message-badges?${qs}`, {
      credentials: 'include',
    })
    if (!res.ok) return {}
    const data = await res.json()
    return data.badges ?? {}
  } catch {
    return {}
  }
}

export async function leaveChannel(channelId, sessionId) {
  const res = await fetch(`${BASE}/channels/${channelId}/leave`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  })
  if (!res.ok) throw new Error('Failed to leave channel')
  return res.json()
}

export async function heartbeat(channelId, sessionId, guestId, nickname) {
  const res = await fetch(`${BASE}/channels/${channelId}/heartbeat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId, guestId, nickname }),
  })
  if (!res.ok) throw new Error('Failed to send heartbeat')
  return res.json()
}

export function disconnectBeacon(sessionId) {
  // sendBeacon is fire-and-forget, reliable on page unload
  const payload = new Blob([JSON.stringify({ sessionId })], { type: 'application/json' })
  navigator.sendBeacon(`${BASE}/disconnect`, payload)
}

export async function fetchChannels(sort = null) {
  const url = sort ? `${BASE}/channels?sort=${encodeURIComponent(sort)}` : `${BASE}/channels`
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch channels')
  return res.json()
}

export async function sendMessage(channelId, sessionId, guestId, nickname, content, replyToMessageId = null, mentions = null) {
  const body = { sessionId, guestId, nickname, content }
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  if (mentions && mentions.length) body.mentions = mentions
  const res = await fetch(`${BASE}/channels/${channelId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to send message')
  return res.json()
}

// Same-origin proxy URL for downloading an R2 image. The R2 public host has no
// CORS headers, so the browser can't fetch the image cross-origin from the web
// app; this routes through the API (which streams it as an attachment).
export function mediaDownloadUrl(url, name) {
  const q = new URLSearchParams({ url })
  if (name) q.set('name', name)
  return `${BASE}/media/download?${q.toString()}`
}

// Downscale + re-compress a photo in the browser before upload so chat images
// aren't multi-MB originals (a phone photo is easily 3-5 MB; the feed loaded the
// full thing). Caps the longest side at 1600px and re-encodes to JPEG q0.82,
// which takes a typical photo to ~200-400 KB with no visible loss. Safe: any
// failure (HEIC the browser can't decode, tiny images, etc.) returns the
// original file untouched.
async function downscaleImage(file, maxDim = 1600, quality = 0.82) {
  try {
    if (!file || !file.type?.startsWith('image/') || file.type === 'image/gif') return file
    // Already small enough → not worth re-encoding.
    if (file.size < 350 * 1024) return file
    const bitmap = await createImageBitmap(file).catch(() => null)
    if (!bitmap) return file
    const { width, height } = bitmap
    const scale = Math.min(1, maxDim / Math.max(width, height))
    const w = Math.round(width * scale)
    const h = Math.round(height * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close?.()
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
    if (!blob || blob.size >= file.size) return file // no win → keep original
    const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg' })
  } catch {
    return file
  }
}

export async function uploadImage(file) {
  const toSend = await downscaleImage(file)
  const form = new FormData()
  form.append('file', toSend)
  // No Content-Type header - browser sets it automatically with the multipart boundary
  const res = await fetch(`${BASE}/uploads`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Upload failed')
  }
  return res.json() // { url, filename }
}

export async function sendImageMessage(channelId, sessionId, guestId, nickname, imageUrl) {
  const res = await fetch(`${BASE}/channels/${channelId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId, guestId, nickname, type: 'image', imageUrl }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send image')
  }
  return res.json()
}

export async function fetchEvents(channelId, sessionId = null) {
  const url = sessionId
    ? `${BASE}/channels/${channelId}/events?sessionId=${sessionId}`
    : `${BASE}/channels/${channelId}/events`
  const res = await fetch(url, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch events')
  return res.json()
}

export async function fetchCityAmbassadors(channelId) {
  try {
    const res = await fetch(`${BASE}/channels/${channelId}/ambassadors`, { credentials: 'include' })
    if (!res.ok) return { ambassadors: [] }
    return res.json() // { ambassadors: UserDTO[] }
  } catch {
    return { ambassadors: [] }
  }
}

export async function fetchCityMembers(channelId, { page = 1, limit = 10, badge = null, vibe = null, mode = null } = {}) {
  const q = new URLSearchParams({ page, limit })
  if (badge) q.set('badge', badge)
  if (vibe)  q.set('vibe',  vibe)
  if (mode)  q.set('mode',  mode)
  const res = await fetch(`${BASE}/channels/${channelId}/members?${q}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch city members')
  return res.json() // { members, total, page, hasMore }
}

export async function fetchCityEvents(channelId) {
  const res = await fetch(`${BASE}/channels/${channelId}/city-events`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch city events')
  return res.json()
}

export async function fetchCityTopics(channelId) {
  try {
    const res = await fetch(`${BASE}/channels/${channelId}/topics`, { credentials: 'include' })
    if (!res.ok) return { topics: [] }
    return res.json()
  } catch {
    return { topics: [] }
  }
}

// ── Normalized now feed ────────────────────────────────────────────────────────
// GET /channels/{id}/now → { items: FeedItem[] }
// Returns a mixed, sorted feed of Hilads events + active topics.
// Both events and topics share consistent top-level fields (kind, title,
// description, active_now, …). Pass sessionId so the backend can annotate
// is_participating on event items.
export async function fetchNowFeed(channelId, sessionId = null, { signal } = {}) {
  try {
    const params = new URLSearchParams()
    if (sessionId) params.set('sessionId', sessionId)
    const qs = params.toString()
    const url = `${BASE}/channels/${channelId}/now${qs ? `?${qs}` : ''}`
    const res = await fetch(url, { credentials: 'include', signal: signal ?? undefined })
    if (!res.ok) return { items: [] }
    return res.json() // { items: FeedItem[] }
  } catch {
    return { items: [] }
  }
}

export async function fetchTopicById(topicId) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch topic')
  return res.json() // { topic, channelId, cityName, country, timezone }
}

export async function fetchTopicMessages(topicId, { beforeId, limit } = {}) {
  const params = new URLSearchParams({ limit: String(limit ?? 50) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/messages?${params}`, { credentials: 'include' })
  // Members-only: a non-member (incl. pending requester) gets 403 - surface it
  // so the page shows the gated "request pending" state instead of erroring.
  // has_pending_request lets the gate render "Requested" on a return visit.
  if (res.status === 403) {
    const body = await res.json().catch(() => ({}))
    return { messages: [], hasMore: false, forbidden: true, hasPendingRequest: !!body.has_pending_request }
  }
  if (!res.ok) throw new Error('Failed to fetch topic messages')
  return res.json() // { messages, hasMore }
}

// ── Hangout request-to-join (internally "topic") ──────────────────────────────

export async function requestToJoinHangout(topicId) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/join-requests`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: '{}',
  })
  const data = await res.json().catch(() => ({}))
  return { status: data.status ?? (res.ok ? 'pending' : 'error'), requestId: data.requestId }
}

export async function resolveHangoutJoinRequest(topicId, requestId, action) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/join-requests/${encodeURIComponent(requestId)}/resolve`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
    body: JSON.stringify({ action }),
  })
  const data = await res.json().catch(() => ({}))
  return { status: data.status ?? (res.ok ? action : 'error'), resolvedByName: data.resolvedByName }
}

export async function sendTopicMessage(topicId, guestId, nickname, content, mentions = null, replyToMessageId = null) {
  const body = { guestId, nickname, content }
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  if (mentions && mentions.length) body.mentions = mentions
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send message')
  }
  return res.json() // { message }
}

export async function sendTopicImageMessage(topicId, guestId, nickname, imageUrl) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, nickname, type: 'image', imageUrl }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send image')
  }
  return res.json() // { message }
}

export async function markTopicRead(topicId, guestId) {
  await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/mark-read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  }).catch(() => {})
}

export async function createTopic(channelId, guestId, title, description, category, coords = null) {
  const body = { guestId, title, description: description || null, category }
  // Hangouts have no address - send the creator's coords so NOW can show distance.
  if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
    body.lat = coords.lat
    body.lng = coords.lng
  }
  const res = await fetch(`${BASE}/channels/${channelId}/topics`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    if (data.error === 'hangout_limit') throw new HangoutLimitError(data.existingTopicId, data.existingTitle)
    throw new Error(data.error || 'Failed to create topic')
  }
  return data
}

// Thrown by createTopic when the user already has an active hangout (one at a time).
export class HangoutLimitError extends Error {
  constructor(existingTopicId, existingTitle) {
    super('hangout_limit')
    this.name = 'HangoutLimitError'
    this.existingTopicId = existingTopicId
    this.existingTitle = existingTitle
  }
}

// Full member list for a hangout (avatar-row modal). Returns { participants, count }.
export async function fetchHangoutParticipants(topicId) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/participants`, { credentials: 'include' })
  if (!res.ok) return { participants: [], count: 0 }
  return res.json()
}

// Owner-only edit of a hangout's title/description/category.
export async function updateTopic(topicId, guestId, title, description, category) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, title, description: description || null, category }),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error(data.error || 'Failed to update hangout')
  return data
}

// Owner-only delete (soft) of a hangout.
export async function deleteTopic(topicId, guestId) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to delete hangout')
  }
}

// ── Challenges (Défis) ────────────────────────────────────────────────────────
// Third primary entity alongside events + hangouts. Persistent (no TTL), with
// an `open` → `validated` lifecycle. Web client supports: read, accept/leave,
// validate (creator), chat. Edit/delete stay mobile-only for now.

export async function fetchChallengeById(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}`)
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch challenge')
  return res.json() // { challenge, channelId, cityName, country, timezone }
}

export async function fetchCityChallenges(channelId, limit = 50) {
  const res = await fetch(`${BASE}/channels/${encodeURIComponent(channelId)}/challenges?limit=${limit}`)
  if (!res.ok) return []
  const data = await res.json()
  return data.challenges ?? []
}

// Read-only "idea book" for the zero-challenge empty state: up to 3 example
// challenges from the most-active OTHER city. The payload carries NO challenge
// id (title/type/creator only), so nothing here can open or take the remote
// challenge. Returns { city, cityId, examples }; empty list on error / when no
// other city qualifies.
export async function fetchChallengeInspiration(excludeChannelId) {
  const empty = { city: null, cityId: null, examples: [] }
  try {
    const res = await fetch(`${BASE}/challenges/inspiration?excludeChannelId=${encodeURIComponent(excludeChannelId)}`)
    if (!res.ok) return empty
    const data = await res.json()
    return { city: data.city ?? null, cityId: data.cityId ?? null, examples: data.examples ?? [] }
  } catch {
    return empty
  }
}

// Read-only "idea book" for the zero-activity events empty state: up to 3
// example hangouts/events from the most-active OTHER city. The payload carries
// NO event id (kind/title/host only), so nothing here can open or join the
// remote event. Returns { city, cityId, examples }; empty list on error / when
// no other city qualifies.
export async function fetchEventInspiration(excludeChannelId) {
  const empty = { city: null, cityId: null, examples: [] }
  try {
    const res = await fetch(`${BASE}/events/inspiration?excludeChannelId=${encodeURIComponent(excludeChannelId)}`)
    if (!res.ok) return empty
    const data = await res.json()
    return { city: data.city ?? null, cityId: data.cityId ?? null, examples: data.examples ?? [] }
  } catch {
    return empty
  }
}

export async function fetchValidatedChallenges(channelId, { limit = 30, before } = {}) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (before) params.set('before', String(before))
  const res = await fetch(`${BASE}/channels/${encodeURIComponent(channelId)}/challenges/validated?${params}`)
  if (!res.ok) return []
  const data = await res.json()
  return data.challenges ?? []
}

export async function fetchChallengeMessages(challengeId, { beforeId, limit } = {}) {
  const params = new URLSearchParams({ limit: String(limit ?? 50) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/messages?${params}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch challenge messages')
  return res.json() // { messages, hasMore }
}

export async function sendChallengeMessage(challengeId, guestId, nickname, content, replyToMessageId = null, mentions = null) {
  const body = { guestId, nickname, content }
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  if (mentions && mentions.length) body.mentions = mentions
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send message')
  }
  return res.json()
}

export async function sendChallengeImageMessage(challengeId, guestId, nickname, imageUrl) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, nickname, type: 'image', imageUrl }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send image')
  }
  return res.json()
}

export async function fetchChallengeParticipants(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/participants`)
  if (!res.ok) return { participants: [], count: 0 }
  return res.json() // { participants, count }
}

export async function toggleChallengeParticipation(challengeId, guestId, nickname = null) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/participants/toggle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, nickname }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to toggle participation')
  }
  return res.json() // { count, isIn }
}

// Optional `intl` carries International-mode fields: targetCityChannelId,
// proofRequirements. Mode itself is not editable - delete + recreate. The
// server ignores both fields on Local rows.
export async function updateChallenge(challengeId, guestId, title, challengeType, audience, returnClause, intl = {}) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      guestId, title, challengeType, audience, returnClause,
      targetCityChannelId: intl.targetCityChannelId ?? null,
      proofRequirements:   intl.proofRequirements ?? null,
      // Preserve the challenge's saved proof type on edit. null = don't change
      // (server COALESCEs); we pass the ORIGINAL validation_method so a title
      // edit never silently resets photo_proof back to the 'meet' form default.
      validationMethod:    intl.validationMethod ?? null,
      // 'public' | 'friends' only; null/omitted = don't change. The server
      // enforces "private not at input" - it's only reachable via the mutual
      // privacy flow.
      visibility:          intl.visibility ?? null,
    }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to update challenge')
    err.code   = data.code  || null
    err.field  = data.field || null
    throw err
  }
  return res.json()
}

// Relaunch an ENDED challenge: reopens it with the SAME countdown originally set
// (server computes the new deadline). Returns the updated challenge.
export async function relaunchChallenge(challengeId, guestId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/relaunch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to relaunch challenge')
    err.code   = data.code || null
    throw err
  }
  return res.json()
}

export async function deleteChallenge(challengeId, guestId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to delete challenge')
  }
}

// Optional `intl` carries International-mode fields: mode, targetCityChannelId,
// proofRequirements. Server defaults mode to 'local' when omitted; Local rows
// ignore the other two.
export async function createChallenge(channelId, guestId, nickname, title, challengeType, audience, returnClause, intl = {}) {
  const res = await fetch(`${BASE}/channels/${encodeURIComponent(channelId)}/challenges`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      guestId, nickname, title, challengeType, audience, returnClause,
      mode:                intl.mode ?? 'local',
      targetCityChannelId: intl.targetCityChannelId ?? null,
      proofRequirements:   intl.proofRequirements ?? null,
      // Validation method: local-only choice ('meet' | 'photo_proof').
      // Server forces 'photo_proof' on International rows regardless.
      validationMethod:    intl.validationMethod ?? null,
      // 'public' | 'friends' only at create-time. Server forces 'public'
      // on International rows regardless of what we send.
      visibility:          intl.visibility ?? 'public',
      // Group model: a local MEET challenge with format='group' carries a meet
      // date + location set at creation.
      format:              intl.format     ?? 'legacy',
      meetAt:              intl.meetAt      ?? null,
      meetEndsAt:          intl.meetEndsAt  ?? null,
      venue:               intl.venue       ?? null,
      venueLat:            intl.venueLat    ?? null,
      venueLng:            intl.venueLng    ?? null,
    }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to create challenge')
    err.code   = data.code  || null
    err.field  = data.field || null
    throw err
  }
  return res.json() // newly-created Challenge object
}

// Daily promo (SPEC 10.13): the one-time ask (yes / later) and the Me screen's
// "Feature my photos" (yes / no). Returns { ok, promoConsent, promoAsk }.
export async function answerPromo(answer) {
  const res = await fetch(`${BASE}/me/promo`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ answer }),
  })
  if (!res.ok) throw new Error('Failed to save')
  return res.json()
}

// Flip users.has_seen_public_optin to TRUE so the first-time public modal
// stops showing. Server returns { ok, hasSeenPublicOptin }.
export async function dismissPublicOptin() {
  const res = await fetch(`${BASE}/me/dismiss-public-optin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to dismiss opt-in')
  }
  return res.json()
}

export async function validateChallenge(challengeId, guestId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/validate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to validate challenge')
  }
  return res.json() // updated challenge object
}

// GROUP challenges: the challenger validates who was present at the meet.
// presentUserIds = the joined takers who showed up. Returns the count + ids.
export async function validatePresence(challengeId, presentUserIds, rating) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/validate-presence`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ presentUserIds, ...(rating ? { rating } : {}) }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to validate presence')
  }
  return res.json() // { ok, present_count, present_ids }
}

// Challenger rates the challenge (stars + optional note) - used by the
// photo-proof reveal modal, which has no validate sheet to capture it.
export async function submitHostRating(challengeId, stars, comment) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/host-rating`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ stars, ...(comment && comment.trim() ? { comment: comment.trim() } : {}) }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to save rating')
  }
  return res.json() // { ok }
}

// The taker's own rating + note (from the reveal modal), stored on their
// acceptance. Mirror of submitHostRating.
export async function submitTakerRating(challengeId, stars, comment) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/taker-rating`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ stars, ...(comment && comment.trim() ? { comment: comment.trim() } : {}) }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to save rating')
  }
  return res.json() // { ok }
}

// Challenger designates the winning photo of a GROUP photo-proof contest. The
// winner earns the +40 bonus and the challenge is marked validated. The backend
// rejects a pick whose user has no submission (error code 'no_submission').
export async function pickWinner(challengeId, winnerUserId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/pick-winner`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ winnerUserId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err = new Error(data.error || 'Failed to pick winner')
    err.code = data.code
    throw err
  }
  return res.json() // { ok, winnerUserId }
}

// Every submitter's photo for a GROUP photo-proof challenge + the winner (if
// picked). Powers the in-channel submissions gallery and the winner picker.
export async function fetchGroupSubmissions(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/submissions`, {
    credentials: 'include',
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to load submissions')
  }
  return res.json() // { submissions: [...], winnerUserId }
}

export async function unvalidateChallenge(challengeId, guestId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/unvalidate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to unvalidate challenge')
  }
  return res.json()
}

// ── PR2 take-on flow ──────────────────────────────────────────────────────────

/** Typed error from POST /accept - carries the backend `code` + (sometimes)
 *  `requiredMode` so UI can show a tailored alert + offer a mode-switch. */
export class AcceptChallengeError extends Error {
  constructor(code, message, requiredMode) {
    super(message)
    this.name = 'AcceptChallengeError'
    this.code = code           // 'not_creator' | 'mode_required' | 'mode_mismatch' | 'cap_reached' (legacy) | 'in_progress'
    this.requiredMode = requiredMode  // 'local' | 'exploring' | undefined
  }
}

export async function acceptChallenge(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    if (data?.code) throw new AcceptChallengeError(data.code, data.error || 'Accept failed', data.required_mode)
    throw new Error(data?.error || `Accept failed (HTTP ${res.status})`)
  }
  return res.json()  // ChallengeAcceptance row
}

// ── International - proof submission ────────────────────────────────────────

export async function fetchProofs(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/proofs`, {
    credentials: 'include',
  })
  if (!res.ok) return { proofs: [], attempts: 0, maxAttempts: 3 }
  return res.json()
}

// PR59 - lat/lng are optional. The proof flow no longer prompts for
// geolocation; the server stubs 0/0 + marks the row verified when no
// coords are sent. Existing callers passing { lat, lng } still work.
export async function submitProof(acceptanceId, { mediaUrl, mediaType, lat, lng }) {
  const payload = { mediaUrl, mediaType }
  if (typeof lat === 'number' && typeof lng === 'number') {
    payload.lat = lat
    payload.lng = lng
  }
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/submit-proof`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(payload),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to submit proof')
  }
  return res.json() // { proof, attempt, maxAttempts }
}

export async function approveProof(proofId) {
  const res = await fetch(`${BASE}/proofs/${encodeURIComponent(proofId)}/approve`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to approve proof')
  }
  return res.json() // { proof }
}

export async function rejectProof(proofId, reason) {
  const res = await fetch(`${BASE}/proofs/${encodeURIComponent(proofId)}/reject`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ reason }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to reject proof')
  }
  return res.json() // { proof, isFinal, attemptsLeft }
}

// ── Personal challenge invitations ──────────────────────────────────────────

export async function inviteToChallenge(challengeId, userIds) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/invite`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userIds }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || `Invite failed (HTTP ${res.status})`)
  }
  return res.json() // { invited: [...], count, duplicates }
}

export async function acceptInvitation(invitationId) {
  const res = await fetch(`${BASE}/invitations/${encodeURIComponent(invitationId)}/accept`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  // Don't throw on 403 - the gate codes (in_progress / mode_mismatch) are
  // meaningful state the caller wants to surface.
  return res.json().catch(() => ({}))
}

export async function ignoreInvitation(invitationId) {
  await fetch(`${BASE}/invitations/${encodeURIComponent(invitationId)}/ignore`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
}

export async function cancelAcceptance(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to cancel')
  }
}

export async function fetchMyAcceptances() {
  const res = await fetch(`${BASE}/me/acceptances`, { credentials: 'include' })
  if (!res.ok) return []
  const data = await res.json().catch(() => ({}))
  return data.threads ?? []
}

// ── PR6: rate-prompts + ratings ──────────────────────────────────────────────

/**
 * Caller's currently rate-eligible meet-ups. Returns [] on network error so
 * the banner just doesn't render (non-blocking surface).
 */
export async function fetchRatePrompts() {
  const res = await fetch(`${BASE}/me/rate-prompts`, { credentials: 'include' })
  if (!res.ok) return []
  const data = await res.json().catch(() => ({}))
  return data.prompts ?? []
}

/**
 * Submit a rating for a challenge. Throws an Error tagged with .status so the
 * UI can branch on 409 (already_rated) / 403 (not_rate_eligible) - both
 * recoverable races: dismiss + refetch the prompts list.
 */
export async function submitRating(challengeId, stars, comment) {
  const body = { stars }
  if (comment && comment.length > 0) body.comment = comment
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/ratings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err = new Error(data?.error || 'Failed to submit rating')
    err.status = res.status
    err.code   = data?.code
    throw err
  }
  return res.json()
}

// ── PR17: score celebration popin ───────────────────────────────────────────

/**
 * Pending "+X points!" celebration delta. Returns { points: 0 } when there's
 * nothing to show (no unacknowledged events) or on network error.
 *
 * Response (when points > 0):
 *   {
 *     points:        number,
 *     event_count:   number,
 *     top_kind:      'accepted'|'date_locked'|'meetup'|'debrief'|'ghost'|null,
 *     seen_until:    string,        // ISO - pass back to ackScoreCelebration
 *     city_id:       string|null,
 *     city_name:     string|null,
 *     city_country:  string|null,   // ISO-2 country, fed into countryToFlag
 *     top_n:         number,        // 100 - beyond this rank is null
 *     rank_alltime:  { city: number|null, global: number|null },
 *     rank_month:    { city: number|null, global: number|null },
 *   }
 */
export async function fetchScoreCelebration() {
  try {
    const res = await fetch(`${BASE}/me/score-celebration`, { credentials: 'include' })
    if (!res.ok) return { points: 0 }
    return await res.json().catch(() => ({ points: 0 }))
  } catch {
    return { points: 0 }
  }
}

// Pending GROUP challenge result reveals (winning photo / present / absent + the
// viewer's points). Each item's `id` is the notification id - ack with
// markNotificationsRead([id]).
export async function fetchChallengeReveals() {
  try {
    const res = await fetch(`${BASE}/me/challenge-reveals`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json().catch(() => ({}))
    return data.reveals ?? []
  } catch {
    return []
  }
}

/**
 * Ack the celebration so the same delta is never shown twice. seen_until is
 * the ISO timestamp returned by the GET above. Fire-and-forget - failure is
 * harmless (worst case the popin re-shows next launch).
 */
export async function ackScoreCelebration(seenUntil) {
  try {
    await fetch(`${BASE}/me/score-celebration/seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ seen_until: seenUntil }),
    })
  } catch {
    /* non-blocking */
  }
}

// ── PR7: leaderboard ────────────────────────────────────────────────────────

/**
 * Fetch the leaderboard for a given scope/period.
 *
 * Returns null on network error so the caller (header chip / screen) can
 * render a neutral state without throwing. The chip uses this to silently
 * fall back to "🏆 Top challengers".
 *
 * opts = { scope: 'city'|'world', period: 'month'|'alltime',
 *          limit?: number, offset?: number, cityId?: string }
 */
export async function fetchLeaderboard(opts) {
  const params = new URLSearchParams({
    scope:  opts.scope,
    period: opts.period,
    limit:  String(opts.limit  ?? 50),
    offset: String(opts.offset ?? 0),
  })
  if (opts.cityId) params.set('city_id', opts.cityId)
  try {
    const res = await fetch(`${BASE}/leaderboard?${params}`, { credentials: 'include' })
    if (!res.ok) return null
    return await res.json()
  } catch (err) {
    console.warn('[fetchLeaderboard] failed:', err)
    return null
  }
}

// Deprecated - kept for the historic mobile build still on the prior
// release. New code uses fetchChallengeMessages on the public challenge
// channel; thread channels are no longer auto-created on accept.
export async function fetchThreadMessages(threadChannelId, { beforeId, limit = 50 } = {}) {
  const params = new URLSearchParams({ limit: String(limit) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/threads/${encodeURIComponent(threadChannelId)}/messages?${params}`, { credentials: 'include' })
  if (!res.ok) return { messages: [], hasMore: false }
  return res.json()
}

export async function sendThreadMessage(threadChannelId, content) {
  const res = await fetch(`${BASE}/threads/${encodeURIComponent(threadChannelId)}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ type: 'text', content }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to send message')
  }
  return res.json()
}

// ── PR3: date concertation ──────────────────────────────────────────────────

export async function proposeDate(acceptanceId, startsAt, endsAt, venue) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/propose-date`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ startsAt, endsAt, venue }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to propose date')
  }
  return res.json()
}

export async function withdrawProposal(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/withdraw-proposal`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to withdraw')
  }
  return res.json()
}

export async function approveDate(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/approve-date`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to approve')
  }
  return res.json()
}

// ── PR4: debrief verdicts ───────────────────────────────────────────────────

export async function approveChallenge(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/approve-challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to approve challenge')
  }
  return res.json()
}

export async function rejectChallenge(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/reject-challenge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to close challenge')
  }
  return res.json()
}

// ── PR5: pending take-on review (creator only) ────────────────────────────
// Creator approves/rejects a pending take-on request. Approve flips the row
// to 'accepted' (chat unlocks for the acceptor); reject flips to 'rejected'
// (acceptor notified, slot reopens).
export async function approveTakeOn(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/approve-takeon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to approve take-on')
  }
  return res.json()
}

export async function rejectTakeOn(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/reject-takeon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to reject take-on')
  }
  return res.json()
}

// Taker leaves an active take-on: deletes the acceptance (challenge reopens
// from zero), wipes the challenge chat, pushes + WS-resets the creator.
export async function abandonAcceptance(acceptanceId) {
  const res = await fetch(`${BASE}/acceptances/${encodeURIComponent(acceptanceId)}/abandon`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to leave challenge')
  }
  return res.json()
}

// Creator restarts from zero: removes the active taker (deletes their
// acceptance), wipes the chat, reopens the challenge, pushes + WS-resets the taker.
export async function restartChallenge(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/restart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data?.error || 'Failed to restart challenge')
  }
  return res.json()
}

export async function fetchUpcomingEvents(channelId, opts = {}) {
  // Backwards-compat: callers used to pass `days` as a positional number.
  // Accept either `fetchUpcomingEvents(id, 14)` or
  // `fetchUpcomingEvents(id, { days, from, to })`.
  if (typeof opts === 'number') opts = { days: opts }
  const params = new URLSearchParams()
  if (opts.from && opts.to) {
    params.set('from', opts.from)
    params.set('to',   opts.to)
  } else {
    params.set('days', String(opts.days ?? 7))
  }
  const res = await fetch(`${BASE}/channels/${channelId}/events/upcoming?${params}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch upcoming events')
  return res.json() // { events: [...] }
}

/**
 * Past archive - finished one-off hangouts + expired pulses for a city.
 * GET /channels/{id}/past → { items: FeedItem[], nextCursor: number|null }
 * Items share the same normalized FeedItem shape as the now feed.
 * `before` is a unix recency cursor (pass the prior page's nextCursor);
 * `from`/`to` are city-local YYYY-MM-DD (backend clamps the span to ≤14 days).
 */
export async function fetchPastArchive(channelId, opts = {}) {
  const params = new URLSearchParams()
  if (opts.type)   params.set('type',  opts.type)
  if (opts.limit)  params.set('limit', String(opts.limit))
  if (opts.before) params.set('before', String(opts.before))
  if (opts.from && opts.to) { params.set('from', opts.from); params.set('to', opts.to) }
  const qs = params.toString()
  try {
    const res = await fetch(`${BASE}/channels/${channelId}/past${qs ? `?${qs}` : ''}`, { credentials: 'include' })
    if (!res.ok) return { items: [], nextCursor: null }
    const data = await res.json()
    return { items: data.items ?? [], nextCursor: data.nextCursor ?? null }
  } catch {
    return { items: [], nextCursor: null }
  }
}

/**
 * Per-day event counts for the calendar-strip dots on the upcoming screen.
 * Returns { "YYYY-MM-DD": count } - days with zero events are omitted.
 */
export async function fetchCalendarSummary(channelId, from, to) {
  const params = new URLSearchParams({ from, to })
  const res = await fetch(`${BASE}/channels/${channelId}/events/calendar-summary?${params}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch calendar summary')
  const data = await res.json()
  return data.summary ?? {}
}

// Thrown by createEvent / createEventSeries when the backend returns the
// structured `event_limit_reached` error. Callers should route to the
// limit-reached screen instead of surfacing a red error.
export class EventLimitReachedError extends Error {
  constructor() {
    super('event_limit_reached')
    this.name = 'EventLimitReachedError'
  }
}

export async function createEvent(channelId, guestId, nickname, title, locationHint, startsAt, endsAt, type, lat, lng) {
  const body = { guestId, nickname, title, starts_at: startsAt, ends_at: endsAt, type }
  if (locationHint) body.location_hint = locationHint
  // Precise coords from the map picker (optional) - power exact Maps links.
  if (typeof lat === 'number' && typeof lng === 'number') { body.lat = lat; body.lng = lng }
  const res = await fetch(`${BASE}/channels/${channelId}/events`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    if (data.error === 'event_limit_reached') throw new EventLimitReachedError()
    throw new Error(data.error || 'Failed to create event')
  }
  return res.json()
}

export async function createEventSeries(channelId, guestId, payload) {
  const res = await fetch(`${BASE}/channels/${channelId}/event-series`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, ...payload }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    if (data.error === 'event_limit_reached') throw new EventLimitReachedError()
    throw new Error(data.error || 'Failed to create event series')
  }
  return res.json() // { series_id, first_event }
}

export async function fetchEventMessages(eventId, { beforeId, limit } = {}) {
  const params = new URLSearchParams({ limit: String(limit ?? 50) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/events/${eventId}/messages?${params}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch event messages')
  return res.json() // { messages, hasMore }
}

export async function fetchEventParticipants(eventId, sessionId) {
  const res = await fetch(`${BASE}/events/${eventId}/participants?sessionId=${sessionId}&lite=1`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch participants')
  return res.json() // { count, isIn }
}

export async function fetchEventGoingList(eventId) {
  const res = await fetch(`${BASE}/events/${eventId}/participants`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch participants')
  return res.json() // { participants: UserDTO[], count, isIn }
}

export async function toggleEventParticipation(eventId, sessionId) {
  const res = await fetch(`${BASE}/events/${eventId}/participants/toggle`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ sessionId }),
  })
  if (!res.ok) throw new Error('Failed to toggle participation')
  return res.json() // { count, isIn }
}

export async function sendEventMessage(eventId, guestId, nickname, content, replyToMessageId = null, mentions = null) {
  const body = { guestId, nickname, content }
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  if (mentions && mentions.length) body.mentions = mentions
  const res = await fetch(`${BASE}/events/${eventId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send message')
  }
  return res.json()
}

// ── @mention autocomplete ──────────────────────────────────────────────────────
// context: 'city' | 'event' | 'topic'. id: city numeric id, or event/topic hex id.
// Registered, in-context users only (backend excludes guests + the caller).
export async function fetchMentionSuggestions(context, id, q) {
  const path = context === 'world' ? `/world/mention-suggestions`
             : context === 'city'  ? `/channels/${id}/mention-suggestions`
             : context === 'event' ? `/events/${id}/mention-suggestions`
             :                       `/topics/${id}/mention-suggestions`
  try {
    const res = await fetch(`${BASE}${path}?q=${encodeURIComponent(q)}`, { credentials: 'include' })
    if (!res.ok) return []
    const data = await res.json()
    return data.suggestions ?? []
  } catch {
    return []
  }
}

// ── Auth & profile ────────────────────────────────────────────────────────────

export async function authSignup(email, password, displayName, username, guestId, mode = null, eulaAccepted = false) {
  const res = await fetch(`${BASE}/auth/signup`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, display_name: displayName, username, guest_id: guestId, mode, eula_accepted: eulaAccepted }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Signup failed')
  return data // { user }
}

// Real-time username availability + format check for the @-handle picker.
// Returns { valid, available, reason }. Sends credentials so the backend
// excludes the caller's own row when editing in profile.
export async function checkUsernameAvailability(username) {
  const res = await fetch(`${BASE}/username/check?username=${encodeURIComponent(username)}`, {
    credentials: 'include',
  })
  if (!res.ok) return { valid: false, available: false, reason: 'Check failed' }
  return res.json()
}

export async function authLogin(email, password) {
  const res = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Login failed')
  return data // { user }
}

export async function authLogout() {
  await fetch(`${BASE}/auth/logout`, { method: 'POST', credentials: 'include' })
}

export async function deleteAccount() {
  const res = await fetch(`${BASE}/auth/me`, { method: 'DELETE', credentials: 'include' })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error ?? 'Failed to delete account')
  }
}

export async function authForgotPassword(email) {
  const res = await fetch(`${BASE}/auth/forgot-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Request failed')
  return data
}

export async function authValidateResetToken(token) {
  const res = await fetch(`${BASE}/auth/reset-password/validate?token=${encodeURIComponent(token)}`, {
    credentials: 'include',
  })
  if (!res.ok) return false
  const data = await res.json()
  return data.valid === true
}

export async function authResetPassword(token, password, passwordConfirmation) {
  const res = await fetch(`${BASE}/auth/reset-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ token, password, passwordConfirmation }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Reset failed')
  return data // { user, token }
}

export async function authMe() {
  const res = await fetch(`${BASE}/auth/me`, { credentials: 'include' })
  if (res.status === 401) return null
  if (!res.ok) throw new Error('Failed to fetch session')
  return res.json() // { user }
}

export async function updateProfile(fields) {
  const res = await fetch(`${BASE}/profile`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(fields),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to update profile')
  return data // { user }
}

export async function fetchPublicProfile(userId) {
  const res = await fetch(`${BASE}/users/${userId}`, { credentials: 'include' })
  if (!res.ok) throw new Error('User not found')
  return res.json() // { user }
}

export async function fetchUserEvents(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/events`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch user events')
  return res.json() // { events }
}

// ── Friends ────────────────────────────────────────────────────────────────────

export async function fetchUserFriends(userId, { page = 1, limit = 20 } = {}) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/friends?page=${page}&limit=${limit}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch friends')
  return res.json() // { friends, total, page, hasMore }
}

// Send a friend request. Backend mutual-add returns { ok: true, friend: true }
// when both users had pending requests to each other (auto-accepted).
export async function sendFriendRequest(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/friends`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({}),
  })
  if (!res.ok) throw new Error('Failed to send friend request')
  return res.json()  // { ok: true, friend?: true, request?: {...} }
}

export async function removeFriend(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/friends`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to remove friend')
  return res.json()
}

export async function acceptFriendRequest(requestId) {
  const res = await fetch(`${BASE}/friend-requests/${encodeURIComponent(requestId)}/accept`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to accept friend request')
  return res.json()
}

export async function declineFriendRequest(requestId) {
  const res = await fetch(`${BASE}/friend-requests/${encodeURIComponent(requestId)}/decline`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to decline friend request')
  return res.json()
}

export async function cancelFriendRequest(requestId) {
  const res = await fetch(`${BASE}/friend-requests/${encodeURIComponent(requestId)}`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to cancel friend request')
  return res.json()
}

export async function fetchIncomingFriendRequests() {
  const res = await fetch(`${BASE}/friend-requests/incoming`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch incoming friend requests')
  const data = await res.json()
  return data.requests ?? []
}

export async function fetchOutgoingFriendRequests() {
  const res = await fetch(`${BASE}/friend-requests/outgoing`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch outgoing friend requests')
  const data = await res.json()
  return data.requests ?? []
}

export async function fetchIncomingFriendRequestCount() {
  const res = await fetch(`${BASE}/friend-requests/incoming-count`, { credentials: 'include' })
  if (!res.ok) return 0
  const data = await res.json()
  return data.count ?? 0
}

// ── Conversations (DMs) ───────────────────────────────────────────────────────

// Active hangouts a user created or joined - for the profile "Hangouts" tab.
export async function fetchUserHangouts(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/hangouts`, { credentials: 'include' })
  if (!res.ok) return { hangouts: [] }
  return res.json() // { hangouts }
}

// Challenges the user created or accepted - for the profile "Challenges"
// tab. Mirrors fetchUserHangouts/fetchUserEvents. Backend already exists
// at GET /users/{userId}/challenges; the mobile app calls it via its own
// helper, this is the web parity.
export async function fetchUserChallenges(userId) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/challenges`, { credentials: 'include' })
  if (!res.ok) return { challenges: [] }
  return res.json() // { challenges }
}

// Public "Success challenges" showcase - completed, well-rated challenges
// (global, or ?cityId). Guest-readable. { items, hasMore }.
export async function fetchChallengeShowcase({ cityId, limit = 30, before } = {}) {
  const q = new URLSearchParams()
  if (cityId) q.set('cityId', String(cityId))
  if (limit)  q.set('limit', String(limit))
  if (before) q.set('before', String(before))
  try {
    const res = await fetch(`${BASE}/challenges/showcase?${q.toString()}`, { credentials: 'include' })
    if (!res.ok) return { items: [], hasMore: false }
    return res.json()
  } catch { return { items: [], hasMore: false } }
}

// The showcase card for ONE challenge, or null when it isn't showcase-eligible
// (private, or no approved proof / rating yet). Powers the World "X won the
// challenge Y" line - tap opens the same preview the success carousel shows.
export async function fetchShowcaseItem(challengeId) {
  if (!challengeId) return null
  try {
    const res = await fetch(`${BASE}/challenges/showcase?challengeId=${encodeURIComponent(challengeId)}`, {
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = await res.json()
    return data.items?.[0] ?? null
  } catch { return null }
}

// 3 real resolved challenges + a who-earned-what point breakdown.
export async function fetchChallengeExamples() {
  try {
    const res = await fetch(`${BASE}/challenges/examples`, { credentials: 'include' })
    if (!res.ok) return []
    return (await res.json()).examples ?? []
  } catch { return [] }
}

export async function fetchMyEvents(guestId) {
  const res = await fetch(`${BASE}/users/me/events?guestId=${encodeURIComponent(guestId)}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch my events')
  return res.json() // { events }
}

// Preflight for the 1-event-per-day rule. Cheap COUNT; safe on every CTA tap.
// Pass an optional `date` (YYYY-MM-DD) to check a specific host day -
// used by the create form when the user picks a non-today date.
// Returns { canCreate, isLegend, todayCount, date, limit }.
export async function fetchCanCreateEvent(channelId, guestId, date) {
  const qs = new URLSearchParams()
  if (channelId) qs.set('channelId', String(channelId))
  if (guestId)   qs.set('guestId',   guestId)
  if (date)      qs.set('date',      date)
  const res = await fetch(`${BASE}/users/me/can-create-event?${qs.toString()}`, { credentials: 'include' })
  if (!res.ok) {
    // On any non-2xx, assume the user CAN create - the POST will enforce the
    // rule server-side and route to the limit screen via the error code.
    return { canCreate: true, isLegend: false, todayCount: 0, limit: 1 }
  }
  return res.json()
}

export async function updateEvent(eventId, guestId, fields) {
  const res = await fetch(`${BASE}/events/${encodeURIComponent(eventId)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, ...fields }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to update event')
  return data // updated event
}

export async function deleteEvent(eventId, guestId) {
  const res = await fetch(`${BASE}/events/${encodeURIComponent(eventId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to delete event')
  }
  return res.json()
}

export async function fetchConversations() {
  const res = await fetch(`${BASE}/conversations`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch conversations')
  return res.json() // { dms, events }
}

export async function fetchConversationsUnread() {
  const res = await fetch(`${BASE}/conversations/unread`, { credentials: 'include' })
  if (res.status === 401) return null   // session gone - caller should clear auth state
  if (!res.ok) throw new Error('Failed to fetch conversations unread')
  return res.json() // { has_unread: bool }
}

export async function createOrGetDirectConversation(targetUserId) {
  const res = await fetch(`${BASE}/conversations/direct`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ targetUserId }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to open conversation')
  return data // { conversation, otherUser }
}

export async function fetchConversationMessages(conversationId, { beforeId, limit } = {}) {
  const params = new URLSearchParams({ limit: String(limit ?? 50) })
  if (beforeId) params.set('before_id', beforeId)
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages?${params}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch messages')
  return res.json() // { messages, hasMore }
}

export async function markConversationRead(conversationId) {
  // Fire-and-forget - UI is already updated optimistically; ignore failures silently.
  await fetch(`${BASE}/conversations/${conversationId}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {})
}

export async function markEventRead(eventId) {
  // Fire-and-forget - ignore failures silently.
  await fetch(`${BASE}/events/${eventId}/mark-read`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => {})
}

export async function sendConversationMessage(conversationId, content, replyToMessageId = null) {
  const body = { content }
  if (replyToMessageId) body.replyToMessageId = replyToMessageId
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || 'Failed to send message')
  return data // { message }
}

export async function sendConversationImageMessage(conversationId, imageUrl) {
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ type: 'image', imageUrl }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send image')
  }
  return res.json() // { message }
}

export async function sendEventImageMessage(eventId, guestId, nickname, imageUrl) {
  const res = await fetch(`${BASE}/events/${eventId}/messages`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ guestId, nickname, type: 'image', imageUrl }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to send image')
  }
  return res.json()
}

// ── Notifications ─────────────────────────────────────────────────────────────

export async function fetchNotifications({ limit = 50, offset = 0 } = {}) {
  const res = await fetch(`${BASE}/notifications?limit=${limit}&offset=${offset}`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch notifications')
  return res.json() // { notifications, unread_count }
}

export async function fetchUnreadCount() {
  const res = await fetch(`${BASE}/notifications/unread-count`, { credentials: 'include' })
  if (res.status === 401) return null   // session gone - caller should clear auth state
  if (!res.ok) throw new Error('Failed to fetch unread count')
  return res.json() // { count }
}

export async function markNotificationsRead(ids) {
  const body = Array.isArray(ids) ? { ids } : { all: true }
  await fetch(`${BASE}/notifications/mark-read`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  }).catch(() => {})
}

export async function fetchNotificationPreferences() {
  const res = await fetch(`${BASE}/notification-preferences`, { credentials: 'include' })
  if (!res.ok) throw new Error('Failed to fetch preferences')
  return res.json() // { preferences }
}

export async function updateNotificationPreferences(prefs) {
  const res = await fetch(`${BASE}/notification-preferences`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(prefs),
  })
  if (!res.ok) throw new Error('Failed to update preferences')
  return res.json() // { preferences }
}

// ── Push subscriptions ────────────────────────────────────────────────────────
// Note: push.js handles the full registration flow (SW + browser permission).
// These helpers are thin HTTP wrappers used internally by push.js.

export async function fetchVapidPublicKey() {
  const res = await fetch(`${BASE}/push/vapid-public-key`, { credentials: 'include' })
  if (!res.ok) return null
  const data = await res.json()
  return data.key ?? null
}

// ── Vibes ─────────────────────────────────────────────────────────────────────

export async function fetchUserVibes(userId, { limit = 20, offset = 0 } = {}) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/vibes?limit=${limit}&offset=${offset}`, {
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to fetch vibes')
  return res.json()
}

export async function postVibe(userId, { rating, message }) {
  const res = await fetch(`${BASE}/users/${encodeURIComponent(userId)}/vibes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ rating, message }),
  })
  if (!res.ok) throw new Error('Failed to post vibe')
  return res.json()
}

// ── Reactions ─────────────────────────────────────────────────────────────────

export async function toggleChannelReaction(channelId, messageId, emoji, guestId) {
  const res = await fetch(`${BASE}/channels/${channelId}/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji, guestId }),
  })
  if (!res.ok) throw new Error('Failed to toggle reaction')
  return res.json() // { reactions: [{emoji, count, self}] }
}

// Toggle a reaction on a Hi-now (topic) message. Same shape / allowed emojis as
// channels; broadcasts to the topic's WS room.
export async function toggleTopicReaction(topicId, messageId, emoji, guestId) {
  const res = await fetch(`${BASE}/topics/${encodeURIComponent(topicId)}/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji, guestId }),
  })
  if (!res.ok) throw new Error('Failed to toggle reaction')
  return res.json() // { reactions: [...] }
}

// PR33 - toggle a reaction on a challenge-channel message. Same shape /
// allowed emojis as channels + events; broadcasts via WS.
export async function toggleChallengeReaction(challengeId, messageId, emoji, guestId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji, guestId }),
  })
  if (!res.ok) throw new Error('Failed to toggle reaction')
  return res.json() // { reactions: [{emoji, count, self}] }
}

export async function toggleEventReaction(eventId, messageId, emoji, guestId) {
  const res = await fetch(`${BASE}/events/${eventId}/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji, guestId }),
  })
  if (!res.ok) throw new Error('Failed to toggle reaction')
  return res.json()
}

export async function toggleDmReaction(conversationId, messageId, emoji) {
  const res = await fetch(`${BASE}/conversations/${conversationId}/messages/${encodeURIComponent(messageId)}/reactions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ emoji }),
  })
  if (!res.ok) throw new Error('Failed to toggle reaction')
  return res.json()
}

// ── Edit / delete (channel: city+event+topic share the same endpoint) ──────
// Backend validates ownership: userId for registered, guestId for guest.
export async function editChannelMessage(messageId, content, guestId = null) {
  const body = { content }
  if (guestId) body.guestId = guestId
  const res = await fetch(`${BASE}/messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to edit message')
  return res.json()
}

export async function deleteChannelMessage(messageId, guestId = null) {
  const body = {}
  if (guestId) body.guestId = guestId
  const res = await fetch(`${BASE}/messages/${encodeURIComponent(messageId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error('Failed to delete message')
  return res.json()
}

// ── DM edit / delete (auth required - sender_id ownership) ─────────────────
export async function editDmMessage(messageId, content) {
  const res = await fetch(`${BASE}/dm-messages/${encodeURIComponent(messageId)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ content }),
  })
  if (!res.ok) throw new Error('Failed to edit DM message')
  return res.json()
}

export async function deleteDmMessage(messageId) {
  const res = await fetch(`${BASE}/dm-messages/${encodeURIComponent(messageId)}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
  if (!res.ok) throw new Error('Failed to delete DM message')
  return res.json()
}


export class DuplicateReportError extends Error {
  constructor(existing) {
    super('already_reported')
    this.name     = 'DuplicateReportError'
    this.existing = existing // { id, created_at, status }
  }
}

export async function submitReport({ reason, guestId, targetUserId, targetGuestId, targetNickname }) {
  const res = await fetch(`${BASE}/reports`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      reason,
      guestId:         guestId       ?? undefined,
      target_user_id:  targetUserId  ?? null,
      target_guest_id: targetGuestId ?? null,
      target_nickname: targetNickname ?? null,
    }),
  })
  if (res.status === 409) {
    const data = await res.json().catch(() => ({}))
    throw new DuplicateReportError(data.existing_report ?? null)
  }
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error ?? 'Failed to submit report')
  }
  return res.json()
}

export async function fetchReportStatus({ guestId, targetUserId, targetGuestId }) {
  const qs = new URLSearchParams()
  if (guestId)       qs.set('guestId',         guestId)
  if (targetUserId)  qs.set('target_user_id',  targetUserId)
  if (targetGuestId) qs.set('target_guest_id', targetGuestId)
  const res = await fetch(`${BASE}/reports/status?${qs.toString()}`, {
    credentials: 'include',
  })
  if (!res.ok) return { reported: false }
  return res.json() // { reported: bool, existing_report?: {...} }
}

export async function fetchLinkPreview(url) {
  const res = await fetch(`${BASE}/link-preview?url=${encodeURIComponent(url)}`, {
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) return null
  const data = await res.json()
  return data?.preview ?? null
}

// ── Challenge privacy (mutual go-private + anonymize-me) ─────────────────────

// Returns:
//   { currentVisibility, myVote, creatorVote, acceptorVote, acceptorUserId,
//     canVote, votes: [...] }
// 404 when caller isn't creator/acceptor; UI should hide the panel in that case.
// ── Challenge participation (join / leave / moderation) ─────────────────────

// Publicly visible channel members (people who clicked Join). Returns
// { members: [{id, displayName, username, thumbAvatarUrl, joinedAt}], count }.
// 404 when the viewer is out of visibility scope on a friends/private row.
export async function fetchChannelParticipants(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/channel-participants`, {
    credentials: 'include',
  })
  if (!res.ok) return { members: [], count: 0 }
  return res.json()
}

// "Am I in?" probe. Returns { isIn, canSubscribe, isKicked, notificationPreference }.
// canSubscribe is wider than isIn: it's true for anyone the challenge_message
// push fan-out reaches, which includes people who only chatted in the channel.
// Anon viewer always gets { isIn:false, reason:'anon' }.
export async function fetchMyChallengeParticipation(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/participants/me`, {
    credentials: 'include',
  })
  if (!res.ok) return { isIn: false }
  return res.json()
}

export async function joinChallenge(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/join`, {
    method: 'POST',
    credentials: 'include',
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to join')
    err.code   = data.code || null
    throw err
  }
  return res.json() // { count, isIn:true }
}

export async function leaveChallenge(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/participants/me`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to leave')
    err.code   = data.code || null
    throw err
  }
  return res.json()
}

export async function kickChallengeParticipant(challengeId, userId, reason = null) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/participants/${encodeURIComponent(userId)}/kick`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(reason ? { reason } : {}),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to remove participant')
    err.code   = data.code || null
    throw err
  }
  return res.json()
}

export async function setChallengeVisibility(challengeId, visibility) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/visibility`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ visibility }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to update visibility')
    err.code   = data.code || null
    throw err
  }
  return res.json()
}

export async function setChallengeCloseToJoins(challengeId, closed) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/close-to-new-joins`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ closed }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to update')
  }
  return res.json()
}

export async function setChallengeNotificationPreference(challengeId, preference) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/notification-preference`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ preference }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to update')
  }
  return res.json()
}

export async function fetchChallengePrivacy(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/privacy`, {
    credentials: 'include',
  })
  if (!res.ok) return null
  return res.json()
}

export async function voteChallengePrivacy(challengeId, vote) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/privacy/vote`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ vote }),
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err  = new Error(data.error || 'Failed to vote')
    err.code   = data.code || null
    throw err
  }
  return res.json() // { ok, myVote, flippedToPrivate, visibility }
}

export async function clearChallengePrivacyVote(challengeId) {
  const res = await fetch(`${BASE}/challenges/${encodeURIComponent(challengeId)}/privacy/vote`, {
    method: 'DELETE',
    credentials: 'include',
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || 'Failed to clear vote')
  }
  return res.json()
}


// App config (GET /app/config): challengeCreationClosed follows the server's
// CHALLENGE_CREATION_CLOSED flag (SPEC 9.10), so the web switches with it.
export async function fetchAppConfig() {
  const res = await fetch(`${BASE}/app/config`)
  if (!res.ok) throw new Error('Failed to fetch app config')
  return res.json() // { minVersion, latestVersion, storeUrls, challengeCreationClosed }
}

// ── Spots map (web map of spots, same API as the app's Map tab) ─────────────
// cityId is the numeric city id ("city_12" is accepted and stripped).

function cityNumber(cityId) {
  if (cityId === null || cityId === undefined) return null
  return String(cityId).replace(/^city_/, '')
}

// Themes v2 keys (hidden_gem, taste...): without it the API answers with the v1 keys;
// themesRev=4: the keys since (get_around), else an older Wednesday key.
// A query parameter, not a header, so GETs stay simple requests (no CORS preflight).
const THEMES_V2 = { themes: '2', themesRev: '4' }

function spotsQuery(params) {
  const q = new URLSearchParams(THEMES_V2)
  Object.entries(params).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') q.set(k, v) })
  return q.toString()
}

/** GET /map: spots (most voted first) in the city or the area, and the city's Hi plans as dated pins. */
/** GET /map/cities: the World map's city bubbles, {city: {id, name, country, lat, lng}, spots}, most spots first. */
export async function fetchMapCities(theme) {
  const res = await fetch(`${BASE}/map/cities?${spotsQuery({ theme })}`)
  if (!res.ok) throw new Error('Failed to fetch map cities')
  return (await res.json()).items ?? []
}

export async function fetchMap({ cityId, bbox, theme }) {
  const res = await fetch(`${BASE}/map?${spotsQuery({
    cityId: cityNumber(cityId),
    bbox:   bbox ? bbox.map(n => n.toFixed(5)).join(',') : null,
    theme,
  })}`)
  if (!res.ok) throw new Error('Failed to fetch map')
  return res.json() // { spots, truncated, plans, city }
}

/** GET /spots/search: spots whose name or dish tags match. */
export async function searchSpots(q, { cityId }) {
  const res = await fetch(`${BASE}/spots/search?${spotsQuery({ q, cityId: cityNumber(cityId) })}`)
  if (!res.ok) throw new Error('Failed to search spots')
  return (await res.json()).items ?? []
}

/** GET /spots/{id}: a spot, its city ranking, photos (paged) and latest captions. */
export async function fetchSpot(id, offset = 0) {
  const res = await fetch(`${BASE}/spots/${encodeURIComponent(id)}?${spotsQuery({ offset: offset || null })}`, { credentials: 'include' })
  if (res.status === 404) return null
  if (!res.ok) throw new Error('Failed to fetch spot')
  return res.json() // { spot, ranking, photos: { items, nextOffset }, captions }
}

// ── Daily Challenge (W1/W2: the web mirrors the app's Today) ────────────────
// Same endpoints as the app (docs/daily-challenge/API.md). Reads send the UI
// language; errors carry the API's status and machine code (err.status/.code).

async function daily(method, path, { params, body } = {}) {
  const q = new URLSearchParams(THEMES_V2)
  Object.entries(params || {}).forEach(([k, v]) => { if (v !== null && v !== undefined && v !== '') q.set(k, v) })
  const res = await fetch(`${BASE}${path}${q.toString() ? `?${q}` : ''}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    const err = new Error(data.error || `HTTP ${res.status}`)
    err.status = res.status
    err.code = data.code || null
    throw err
  }
  return res.status === 204 ? null : res.json()
}

const cityNum = (cityId) => (cityId ? String(cityId).replace(/^city_/, '') : null)

/** GET /daily/today: today's challenge, the city's Local Twist, my proof, streak… */
/** `here`: a guest's own city ({cityId, gps}); an account's comes from the server. */
export function fetchDailyToday(cityId, locale, here = null) {
  return daily('GET', '/daily/today', { params: {
    cityId: cityNum(cityId), locale,
    hereId: here ? cityNum(here.cityId) : undefined, hereSource: here ? (here.gps ? 'gps' : 'ip') : undefined,
  } })
}

/** GET /daily/challenges/{date}: the sheet (proofs paged by `before`, results once closed). */
export async function fetchDailySheet(date, { scope = 'world', cityId, before, limit = 20, locale } = {}) {
  const s = await daily('GET', `/daily/challenges/${date}`, { params: { scope, cityId: cityNum(cityId), before, limit, locale } })
  if (!before) dailyCache.set(`sheet|${date}|${scope}|${cityNum(cityId) ?? ''}|${locale}`, s)
  return s
}

/** What the world said: one row per city (SPEC 10.6). Public. */
export async function fetchDailyBoard(date, locale) {
  const b = await daily('GET', `/daily/challenges/${date}/compare`, { params: { locale } })
  dailyCache.set(`board|${date}|${locale}`, b)
  return b
}

// The last sheet and board per date (Today prefetches today's): the challenge sheet shows them at once, then refreshes.
const dailyCache = new Map()
export const cachedDailySheet = (date, { scope = 'world', cityId, locale } = {}) => dailyCache.get(`sheet|${date}|${scope}|${cityNum(cityId) ?? ''}|${locale}`) ?? null
export const cachedDailyBoard = (date, locale) => dailyCache.get(`board|${date}|${locale}`) ?? null


/** Yesterday around the world (SPEC 10.7): one slide per city of yesterday's challenge. Public. */
export function fetchDailyTour(cityId, locale) {
  return daily('GET', '/daily/tour', { params: { cityId: cityNum(cityId), locale } })
}

/** The world, compared (SPEC 10.9): comparisons by shared key, last week (Today's carousel) or all time (the Index screen). */
export function fetchCompared(scope, locale) {
  return daily('GET', '/daily/compared', { params: { scope, locale } })
}

/** The day's reference exchange rates, EUR base (SPEC 10.5). */
export function fetchDailyRates() {
  return daily('GET', '/daily/rates')
}

/** Yesterday's podium: the scope's top 3 proofs of yesterday, total and countries. A city with none falls back to World. */
export function fetchDailyPodium({ scope = 'world', cityId, locale } = {}) {
  return daily('GET', '/daily/podium', { params: { scope, cityId: cityNum(cityId), locale } })
}

/** Past challenges, newest first (scope world or city), paged by date, optionally one theme. */
export function fetchDailyArchive({ scope = 'world', cityId, before, theme, limit = 10, locale } = {}) {
  return daily('GET', '/daily/archive', { params: { scope, cityId: cityNum(cityId), before, theme, limit, locale } })
}

/** Today's proof strip: the scope's proofs (most voted, then newest), total and countries. A city with none falls back to World. */
/** order 'new': Today's photos (newest first, players' photos only, up to 60). */
export function fetchDailyTodayProofs({ scope = 'world', cityId, limit = 12, locale, order } = {}) {
  return daily('GET', '/daily/today/proofs', { params: { scope, cityId: cityNum(cityId), limit, locale, order } })
}

export async function fetchDailyProof(id, locale) {
  return (await daily('GET', `/daily/proofs/${id}`, { params: { locale } })).proof
}

/** Week strip / calendar: days from..to with my status, and my streak. */
export function fetchDailyCalendar(from, to) {
  return daily('GET', '/daily/calendar', { params: { from, to } })
}

/** Provisional leaderboard of the active challenge. */
export function fetchDailyLeaderboard() {
  return daily('GET', '/daily/leaderboard')
}

/** Vote / withdraw my vote (3 per challenge). → { voted, votesLeft, voteCount, validatedByLocal } */
export function voteDailyProof(id)   { return daily('POST', `/daily/proofs/${id}/vote`, { body: {} }) }
export function unvoteDailyProof(id) { return daily('DELETE', `/daily/proofs/${id}/vote`) }
/** Who voted for my photo, newest first (its author only). → [{ id, username, displayName, photoUrl, city, isLocal }] */
export async function fetchDailyVoters(id) { return (await daily('GET', `/daily/proofs/${id}/voters`)).items }

/** A day's players' photos by country (Today's stories and mosaic, "See all", the challenge's bubbles); `date` a past day, default today. */
export function fetchDailyTodayCountries({ date, cityId, locale } = {}) {
  return daily('GET', '/daily/today/countries', { params: { date, cityId: cityNum(cityId), locale } })
}
/** The photos I've just viewed (signed in): at most 100 ids per call. */
export function markDailyProofsSeen(ids) { return daily('POST', '/daily/proofs/seen', { body: { ids } }) }
/** These approved photos (1 to 50 ids), with my votes. */
export async function fetchDailyProofsByIds(ids, locale) {
  return (await daily('GET', '/daily/proofs', { params: { ids: ids.join(','), locale } })).items
}

/** Report a visible proof. 409 = already reported. */
export function reportDailyProof(id, reasonCode) {
  return daily('POST', `/daily/proofs/${id}/report`, { body: { reasonCode } })
}

// ── Comments on Daily photos (one thread per photo, oldest first) ─────────

/** A page of a photo's comments: the latest, or the ones before a comment id. → {items, hasMore, commentCount} */
export function fetchPhotoComments(proofId, before) {
  return daily('GET', `/daily/proofs/${proofId}/comments`, { params: { before } })
}
/** Comment a photo (signed in). → {comment, commentCount}; err.code moderation_blocked, rate_limited, invalid_body, invalid_reply */
export function postPhotoComment(proofId, body, replyToId) {
  return daily('POST', `/daily/proofs/${proofId}/comments`, { body: { body, replyToId: replyToId ?? undefined } })
}
export function deletePhotoComment(id) { return daily('DELETE', `/daily/comments/${id}`) }
export function likePhotoComment(id, on) { return daily(on ? 'POST' : 'DELETE', `/daily/comments/${id}/like`, on ? { body: {} } : {}) }
/** 409 = already reported. */
export function reportPhotoComment(id, reasonCode) { return daily('POST', `/daily/comments/${id}/report`, { body: { reasonCode } }) }
/** @ autocomplete in a photo's comments: its author and thread first. Never throws. */
export async function fetchPhotoMentionSuggestions(proofId, q) {
  try { return (await daily('GET', `/daily/proofs/${proofId}/mention-suggestions`, { params: { q } })).suggestions ?? [] } catch { return [] }
}

/** Proposals (signed in): a challenge, mine, withdraw. */
export async function proposeChallenge(input) {
  return (await daily('POST', '/daily/proposals', { body: input })).proposal
}
export function fetchMyProposals(locale) { return daily('GET', '/daily/proposals/mine', { params: { locale } }) }
export function withdrawDailyProposal(id) { return daily('DELETE', `/daily/proposals/${id}`) }

/** Tomorrow's Local Twist proposal (locals of a live city). */
export function fetchTwistProposal(locale) { return daily('GET', '/daily/twist-proposal', { params: { locale } }) }
export async function proposeTwist(text)  { return (await daily('POST', '/daily/twist-proposal', { body: { text } })).proposal }
export async function editTwistProposal(text) { return (await daily('PATCH', '/daily/twist-proposal', { body: { text } })).proposal }
export function withdrawTwistProposal()   { return daily('DELETE', '/daily/twist-proposal') }

/** The nearest spots (Today's "Spots near you"); fallback: the nearest anywhere. */
export function fetchSpotsNear({ lat, lng, cityId }) {
  return daily('GET', '/spots/near', { params: {
    lat: lat !== undefined ? lat.toFixed(4) : null, lng: lng !== undefined ? lng.toFixed(4) : null, cityId: cityNum(cityId),
  } })
}

/** GET /channels/{cityId}/people (V3-04): Hi now, tonight's plans, arrivals, here now; counts below `min` are null. */
export function fetchPeopleCounts(cityId) {
  return daily('GET', `/channels/${cityNum(cityId)}/people`)
}

/** Live vote state of up to 50 proofs (the chat's Daily cards), plus my votes left. */
export function fetchDailyProofStates(ids) {
  return daily('GET', '/daily/proofs/state', { params: { ids: ids.join(',') } })
}

// ── Passport (W4, like the app's Passport tab) ─────────────────────────────

/** Daily Challenge profile stats: streaks, completed, Local Pass progress per city. */
export function fetchUserDaily(userId, locale) {
  return daily('GET', `/users/${encodeURIComponent(userId)}/daily`, { params: { locale } })
}

/** GET /me/daily-photos: my Daily Challenge photos, newest first (Passport's "My photos"). */
export async function fetchMyDailyPhotos(locale) {
  return (await daily('GET', '/me/daily-photos', { params: { locale } })).items ?? []
}

/** GET /me/spots: the spots where I have an approved proof. */
export async function fetchMySpots() {
  return (await daily('GET', '/me/spots')).items ?? []
}

/** My weekly Most Local rank (GET /leaderboard/week); rank null without points this week. */
export async function fetchWeeklyRank(cityId) {
  const params = cityId ? { scope: 'city', cityId: cityNum(cityId), limit: 1 } : { scope: 'world', limit: 1 }
  return (await daily('GET', '/leaderboard/week', { params })).me
}
